<template>
  <div class="create-section">
    <div class="sub-nav-container">
      <!-- 子导航栏 -->
      <div class="sub-nav-launch">
        <button class="sub-nav-btn" :class="{ active: currentSubTab === 'new' }" @click="switchSubTab('new')">
          新内容
        </button>
        <button class="sub-nav-btn" :class="{ active: currentSubTab === 'moderation' }"
          @click="switchSubTab('moderation')">
          审核中
        </button>
        <button class="sub-nav-btn" :class="{ active: currentSubTab === 'suggested' }"
          @click="switchSubTab('suggested')">
          推荐心情
        </button>
        <button class="sub-nav-btn" :class="{ active: currentSubTab === 'declined' }" @click="switchSubTab('declined')">
          已拒绝
        </button>
      </div>
      <div class="sub-nav-launch" v-if="currentSubTab !== 'new'">
        <el-select v-model="optionsValue" placeholder="请选择">
          <el-option v-for="item in optionsList" :key="item.value" :label="item.label" :value="item.value"
            @change="handleChange(item)">
          </el-option>
        </el-select>
      </div>
    </div>

    <div class="form-container">
      <!-- 新内容表单 -->
      <div v-if="currentSubTab === 'new'">
        <div class="warning-banner" v-if="dailyLimitReached && remainingCount.available <= 0">
          <i class="el-icon-warning"></i>
          <span>The daily limit for creating icebreakers reached.</span>
        </div>

        <!-- 选择类型 -->
        <div class="form-section">
          <label class="form-label">选择类型*</label>
          <div class="radio-group">
            <label class="radio-item" :class="{ active: formData.type === 'chat' }">
              <input type="radio" v-model="formData.type" value="chat">
              <span class="radio-custom"></span>
              <span class="radio-text">聊天</span>
            </label>
            <label class="radio-item" :class="{ active: formData.type === 'mail' }">
              <input type="radio" v-model="formData.type" value="mail">
              <span class="radio-custom"></span>
              <span class="radio-text">邮件</span>
            </label>
          </div>
        </div>

        <!-- 创建消息和图片上传区域 -->
        <div class="form-section">
          <!-- 标签行 -->
          <div class="labels-row">
            <label class="form-label">
              创建消息*
              <i class="el-icon-info"></i>
            </label>
          </div>

          <div class="message-and-image-container" :class="{ 'with-image': showImageUpload }">
            <!-- 消息输入区域 -->
            <div class="message-input-section" :class="{ 'full-width': !showImageUpload }">
              <div class="message-input-container">
                <textarea v-model="formData.message" class="message-input" placeholder="Type your message here"
                  :maxlength="formData.type === 'chat' ? 300 : 1000"></textarea>
                <div class="message-footer">
                  <span class="emoji-picker">
                    <emoji-picker ref="emojiPicker" :on-insert-emoji="insertEmoji" />
                  </span>
                  <span class="char-count">{{ formData.message.length }}/{{ formData.type === 'chat' ? 300 : 1000
                  }}</span>
                </div>
              </div>
            </div>

            <!-- 图片上传区域 -->
            <div v-if="showImageUpload" class="image-upload-section">
              <div class="image-upload-container">
                <!-- 上传区域 - 只在没有选择图片时显示 -->
                <div v-if="formData.images.length === 0" class="upload-area" @click="openAttachmentModal"
                  :class="{ disabled: !canAddImage }">
                  <div class="upload-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                  </div>
                  <div class="upload-text">{{ uploadButtonText }}</div>
                  <div class="upload-hint">最多可上传1张图片</div>
                </div>

                <!-- 已选择的图片预览 -->
                <div v-for="(image, index) in formData.images" :key="`img-${index}`" class="image-preview">
                  <img :src="getMediaUrl(image)" :alt="`图片 ${index + 1}`" />
                  <button class="remove-image" @click="removeImage(index)" title="删除图片">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 选择心情 -->
        <div class="form-section" v-if="formData.type === 'chat'">
          <label class="form-label">选择一个或多个心情来反映RUs的意图*</label>
          <div class="mood-selectors">
            <div class="mood-selector-item" @click="toggleMood('realLove')">
              <div class="mood-label real-love" :class="{ selected: formData.moods.realLove }">
                <i class="mood-icon">❤️</i>
                Real love
              </div>
            </div>
            <div class="mood-selector-item" @click="toggleMood('friendship')" v-if="canSelectFriendship">
              <div class="mood-label friendship" :class="{ selected: formData.moods.friendship }">
                <i class="mood-icon">🤝</i>
                Friendship
              </div>
            </div>
            <div class="mood-selector-item disabled" v-else>
              <div class="mood-label friendship disabled">
                <i class="mood-icon">🤝</i>
                Friendship
                <span class="disabled-hint">(仅限"所有人")</span>
              </div>
            </div>
            <div class="mood-selector-item" @click="toggleMood('hotTalks')">
              <div class="mood-label hot-talks" :class="{ selected: formData.moods.hotTalks }">
                <i class="mood-icon">💋</i>
                Hot talks
              </div>
            </div>
            <div class="select-all" @click="toggleSelectAll">
              {{ isAllSelected ? '取消全选' : `全选 (${getAvailableMoods().length}个)` }}
            </div>
          </div>
        </div>

        <!-- 选择接收者 -->
        <div class="form-section" v-if="formData.type === 'chat'">
          <label class="form-label">选择接收者*</label>
          <div class="radio-group">
            <label class="radio-item" :class="{ active: formData.gender === 'female' }">
              <input type="radio" v-model="formData.gender" value="female">
              <span class="radio-custom"></span>
              <span class="radio-text">女性</span>
            </label>
            <label class="radio-item" :class="{ active: formData.gender === 'male' }">
              <input type="radio" v-model="formData.gender" value="male">
              <span class="radio-custom"></span>
              <span class="radio-text">男性</span>
            </label>
            <label class="radio-item" :class="{ active: formData.gender === 'everyone' }">
              <input type="radio" v-model="formData.gender" value="everyone">
              <span class="radio-custom"></span>
              <span class="radio-text">所有人</span>
            </label>
          </div>
        </div>


        <!-- 底部操作栏 -->
        <div class="bottom-bar">
          <div class="limit-info">
            剩余可创建的Icebreakers数量: {{ remainingCount.available }}/{{ remainingCount.total }}
          </div>
          <button class="submit-btn" :class="{ disabled: !isFormValid }" @click="submitForModeration"
            :disabled="!isFormValid">
            <i class="el-icon-s-promotion"></i>
            发送以供审核
          </button>
        </div>
      </div>

      <!-- 审核中列表 -->
      <div v-if="currentSubTab === 'moderation'" class="list-container" @scroll="handleScroll">
        <!-- 空状态提示 -->
        <div v-if="!loading && moderationList.length === 0" class="empty-state">
          <i class="el-icon-document"></i>
          <p>暂无审核中的数据</p>
          <span>您还没有提交任何内容进行审核</span>
        </div>
        <div class="icebreaker-card" v-for="item in moderationList" :key="item.id">
          <div class="card-left">
            <div class="card-header">
              <div class="card-col">
                <span class="col-label">创建时间</span>
                <span class="col-value">{{ formatDate(item.created) }}</span>
              </div>
              <div class="card-col">
                <span class="col-label">类型</span>
                <span class="col-value">
                  <i :class="item.type === 'mail' ? 'el-icon-message' : 'el-icon-chat-dot-round'"></i>
                  {{ item.type === 'mail' ? '邮件' : '聊天' }}
                </span>
              </div>
              <div class="card-col">
                <span class="col-label">性别</span>
                <span class="col-value">
                  <i :class="getGenderIconClass(item.gender)" :title="getGenderTooltip(item.gender)"></i>
                </span>
              </div>
              <div class="card-col message-col">
                <span class="col-label">消息</span>
                <div class="message-content">
                  <p class="message-text">{{ item.message }}</p>
                  <!-- 图片附件显示 -->
                  <div v-if="item.attachments && item.attachments.length > 0" class="attachment-images">
                    <div v-for="(attachment, index) in item.attachments" :key="index" class="attachment-item">
                      <div v-if="attachment.type === 'photo'" class="image-attachment">
                        <imagePreview :src="attachment.url" :alt="`附件图片 ${index + 1}`"
                          :previewSrcList="getImageUrls(item.attachments)" :currentIndex="index"
                          class="attachment-image" />
                      </div>
                    </div>
                  </div>
                  <div class="mood-tags">
                    <span class="mood-tag" v-for="mood in item.moods" :key="mood.type" :class="mood.type">
                      <i class="mood-icon" :class="getMoodIconClass(mood.type)"></i>
                      {{ mood.label }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-right">
            <div class="status-badge pending">
              <span class="status-text">审核中</span>
            </div>
          </div>
        </div>
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-more">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        <div v-else-if="!hasMore && moderationList.length > 0" class="no-more">
          <span>没有更多数据了</span>
        </div>
      </div>

      <!-- 推荐心情列表 -->
      <div v-if="currentSubTab === 'suggested'" class="list-container" @scroll="handleScroll">
        <!-- 空状态提示 -->
        <div v-if="!loading && suggestedList.length === 0" class="empty-state">
          <i class="el-icon-star-on"></i>
          <p>暂无推荐心情数据</p>
          <span>您还没有获得推荐的心情内容</span>
        </div>
        <div class="icebreaker-card" v-for="item in suggestedList" :key="item.id">
          <div class="card-left">
            <div class="card-header">
              <div class="card-col">
                <span class="col-label">创建时间</span>
                <span class="col-value">{{ formatDate(item.created) }}</span>
              </div>
              <div class="card-col">
                <span class="col-label">类型</span>
                <span class="col-value">
                  <i :class="item.type === 'mail' ? 'el-icon-message' : 'el-icon-chat-dot-round'"></i>
                  {{ item.type === 'mail' ? '邮件' : '聊天' }}
                </span>
              </div>
              <div class="card-col">
                <span class="col-label">性别</span>
                <span class="col-value">
                  <i :class="getGenderIconClass(item.gender)" :title="getGenderTooltip(item.gender)"></i>
                </span>
              </div>
              <div class="card-col message-col">
                <span class="col-label">消息</span>
                <div class="message-content">
                  <p class="message-text">{{ item.message }}</p>
                  <!-- 图片附件显示 -->
                  <div v-if="item.attachments && item.attachments.length > 0" class="attachment-images">
                    <div v-for="(attachment, index) in item.attachments" :key="index" class="attachment-item">
                      <div v-if="attachment.type === 'photo'" class="image-attachment">
                        <imagePreview :src="attachment.url" :alt="`附件图片 ${index + 1}`"
                          :previewSrcList="getImageUrls(item.attachments)" :currentIndex="index"
                          class="attachment-image" />
                      </div>
                    </div>
                  </div>
                  <div class="mood-tags">
                    <span class="mood-tag" v-for="mood in item.moods" :key="mood.type" :class="mood.type">
                      <i class="mood-icon" :class="getMoodIconClass(mood.type)"></i>
                      {{ mood.label }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-right">
            <div class="status-badge approved">
              <span class="status-text">已推荐</span>
            </div>
          </div>
        </div>
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-more">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        <div v-else-if="!hasMore && suggestedList.length > 0" class="no-more">
          <span>没有更多数据了</span>
        </div>
      </div>

      <!-- 已拒绝列表 -->
      <div v-if="currentSubTab === 'declined'" class="list-container" @scroll="handleScroll">
        <!-- 空状态提示 -->
        <div v-if="!loading && declinedList.length === 0" class="empty-state">
          <i class="el-icon-warning"></i>
          <p>暂无已拒绝数据</p>
          <span>您还没有被拒绝的内容</span>
        </div>
        <div class="icebreaker-card" v-for="item in declinedList" :key="item.id">
          <div class="card-left">
            <div class="card-header">
              <div class="card-col">
                <span class="col-label">创建时间</span>
                <span class="col-value">{{ formatDate(item.created) }}</span>
              </div>
              <div class="card-col">
                <span class="col-label">类型</span>
                <span class="col-value">
                  <i :class="item.type === 'mail' ? 'el-icon-message' : 'el-icon-chat-dot-round'"></i>
                  {{ item.type === 'mail' ? '邮件' : '聊天' }}
                </span>
              </div>
              <div class="card-col">
                <span class="col-label">性别</span>
                <span class="col-value">
                  <i :class="getGenderIconClass(item.gender)" :title="getGenderTooltip(item.gender)"></i>
                </span>
              </div>
              <div class="card-col message-col">
                <span class="col-label">消息</span>
                <div class="message-content">
                  <p class="message-text">{{ item.message }}</p>
                  <!-- 图片附件显示 -->
                  <div v-if="item.attachments && item.attachments.length > 0" class="attachment-images">
                    <div v-for="(attachment, index) in item.attachments" :key="index" class="attachment-item">
                      <div v-if="attachment.type === 'photo'" class="image-attachment">
                        <imagePreview :src="attachment.url" :alt="`附件图片 ${index + 1}`"
                          :previewSrcList="getImageUrls(item.attachments)" :currentIndex="index"
                          class="attachment-image" />
                      </div>
                    </div>
                  </div>
                  <div class="mood-tags">
                    <span class="mood-tag" v-for="mood in item.moods" :key="mood.type" :class="mood.type">
                      <!-- <i class="mood-icon" :class="getMoodIconClass(mood.type)"></i> -->
                      {{ mood.label }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-right">
            <div class="status-badge declined">
              <span class="status-text">被AI拒绝</span>
              <div class="decline-reason">
                <span class="reason-text">质量要求未达到</span>
              </div>
              <button class="disagree-btn" @click="disagreeIcebreakersFun(item.id)">
                <i class="el-icon-refresh"></i>
                重新提交
              </button>
            </div>
          </div>
        </div>
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-more">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        <div v-else-if="!hasMore && declinedList.length > 0" class="no-more">
          <span>没有更多数据了</span>
        </div>
      </div>
    </div>

    <!-- Attachment组件 -->
    <attachment ref="attachmentDom" :chatUserInfo="attachmentUserInfo" :approvedOnly="true"
      @send-sonMediaMessage="handleAttachmentSelection" @visible-change="handleAttachmentVisibleChange"
      :currentMessageType="currentMessageType" :hideAudio="true" :enableContinuousLoad="true" />
  </div>
</template>

<script>
import { getIcebreakersLimits, getIcebreakersList, getIcebreakersCountByStatus, createIcebreakers, disagreeIcebreakers } from '@/api/talkytimes/icebreakers'
import EmojiPicker from '@/views/talkytimes/views/message/components/emoji-picker.vue'
import { getFeatureFlagList } from '@/api/talkytimes/message'
import attachment from '@/views/talkytimes/views/masSending/components/attachment.vue'
import imagePreview from '@/components/imagePreview.vue'
export default {
  name: 'CreateIcebreaker',
  components: {
    EmojiPicker,
    attachment,
    imagePreview
  },
  props: {
    selectedUser: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      optionsList: [
        { value: 'all', label: '全部' },
        { value: 'mail', label: '邮件' },
        { value: 'chat', label: '聊天' }
      ],
      optionsValue: 'all',
      remainingCount: {
        total: 5,
        available: 4
      },
      currentSubTab: 'new',
      dailyLimitReached: false,
      formData: {
        type: 'chat',
        message: '',
        moods: {
          realLove: false,
          romance: false,
          flirt: false,
          friendship: false,
          hotTalks: false,
          notSure: false
        },
        gender: 'everyone',
        images: []
      },
      // 审核中列表
      moderationList: [],
      // 推荐心情列表
      suggestedList: [],
      // 已拒绝列表
      declinedList: [],
      // 分页相关数据
      lastId: 0,
      hasMore: true,
      loading: false,
      icebreakerWithPhoto: false,
      // 附件相关数据
      attachmentUserInfo: null,
      currentMessageType: 'chat'
    }
  },
  computed: {
    isFormValid() {
      // 检查消息内容是否填写
      const hasMessage = this.formData.message.trim();

      if (this.formData.type === 'mail') {
        // 邮件类型：只需要有消息内容
        return hasMessage;
      } else {
        // 聊天类型：需要消息内容、选择心情、选择接收者
        const availableMoods = this.getAvailableMoods();
        const hasSelectedMood = availableMoods.some(mood => this.formData.moods[mood] === true);
        return hasMessage && hasSelectedMood && this.formData.gender;
      }
    },
    isAllSelected() {
      const availableMoods = this.getAvailableMoods();
      return availableMoods.every(mood => this.formData.moods[mood]);
    },
    canSelectFriendship() {
      return this.formData.gender === 'everyone';
    },
    showImageUpload() {
      return this.icebreakerWithPhoto && this.formData.type === 'chat';
    },
    // 检查是否可以添加图片
    canAddImage() {
      return this.formData.type === 'chat' && this.formData.images.length < 1;
    },
    // 上传按钮文本
    uploadButtonText() {
      return this.formData.images.length >= 1 ? '已达到上限' : '添加图片';
    }
  },
  watch: {
    selectedUser: {
      handler(newUser, oldUser) {
        // 当用户切换时，重新获取限制数据和列表数据
        // 设置附件用户信息
        this.attachmentUserInfo = {
          ...newUser,
          platformUserId: newUser.id,
        };
        if (newUser && newUser !== oldUser) {
          console.log('CreateIcebreaker: 用户切换，重新获取数据', newUser)
          console.log('当前页签:', this.currentSubTab)
          this.getIcebreakersLimitsFun()

          // 如果当前不在新建页签，重新获取列表数据
          if (this.currentSubTab !== 'new') {
            console.log('重新获取列表数据，页签:', this.currentSubTab)
            this.resetListData()
            this.getIcebreakersListFun(this.currentSubTab)
          } else {
            console.log('当前在新建页签，不重新获取列表数据')
          }
        }
      },
      immediate: true
    },
    'formData.gender'(newGender) {
      // 当性别不是"所有人"时，自动取消友谊的选择
      if (newGender !== 'everyone' && this.formData.moods.friendship) {
        this.formData.moods.friendship = false;
      }
    },
    'formData.type'(newType) {
      if (newType === 'mail') {
        this.formData.images = [];
      }
    },
    currentSubTab(newTab) {
      // this.updateOptionsList()
      this.optionsValue = 'all'
    },
    optionsValue(newVal) {
      // 只有在非 new 页签下才请求
      if (this.currentSubTab !== 'new') {
        // 重置分页状态
        this.lastId = 0
        this.hasMore = true
        this.getIcebreakersListFun(this.currentSubTab)
      }
    }
  },
  mounted() {
    // 移除checkDailyLimit调用，因为watch监听器会处理
    // this.updateOptionsList()
  },
  methods: {
    disagreeIcebreakersFun(id) {
      this.$confirm('确定重新提交吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then((res) => {
        if (res) {
          disagreeIcebreakers({ id: id }).then(res => {
            console.log(res, '重新提交')
            this.$message.success('重新提交成功')
            this.getIcebreakersListFun('declined')
          })
        } else {
          this.$message.error('取消重新提交')
        }
      })
    },
    switchSubTab(tab) {
      this.currentSubTab = tab
      // 重置分页状态
      this.lastId = 0
      this.hasMore = true

      if (tab === 'moderation') {
        this.optionsValue = 'all'
        this.getIcebreakersListFun('moderation')
      } else if (tab === 'suggested') {
        this.getIcebreakersListFun('suggested')
      } else if (tab === 'declined') {
        this.getIcebreakersListFun('declined')
      }
    },
    // getIcebreakersCountByStatus() {
    //   const statuses = ['suggested']
    //   getIcebreakersCountByStatus({ statuses }).then(res => {
    //     console.log('统计数据:', res)
    //     // 这里可以根据接口返回的数据更新optionsList的显示
    //     this.optionsList = res.map(item => ({
    //       value: item.status,
    //       label: item.status == 'suggested_by_ai' ? 'AI' : '人工'
    //     }))
    //   }).catch(err => {
    //     console.error('获取统计数据失败:', err)
    //   })
    // },
    handleChange(item) {
      console.log(item, 'item')
    },
    // 重置列表数据
    resetListData() {
      // 清空所有列表数据
      this.moderationList = []
      this.suggestedList = []
      this.declinedList = []

      // 重置分页状态
      this.lastId = 0
      this.hasMore = true

      // 重置加载状态
      this.loading = false
    },
    getIcebreakersLimitsFun() {
      // 确保有选中的用户
      if (!this.selectedUser || !this.selectedUser.id) {
        console.error('没有选中用户，无法获取限制数据')
        this.$message.error('请先选择一个用户')
        return
      }

      // 传递当前选中用户的ID
      const params = {
        idTrustedUser: this.selectedUser.id
      }

      // console.log('获取限制数据，用户ID:', this.selectedUser.id)

      getIcebreakersLimits(params).then(res => {
        // console.log(res, '限制数量')
        this.remainingCount = res
        this.dailyLimitReached = this.remainingCount.available <= 0
      })
      getFeatureFlagList().then(res => {
        console.log('限制:', res)
        this.icebreakerWithPhoto = res.icebreakerWithPhoto
      })
    },
    getIcebreakersListFun(status, isLoadMore = false) {
      console.log('getIcebreakersListFun 被调用，状态:', status, '是否加载更多:', isLoadMore)

      if (this.loading) {
        console.log('正在加载中，跳过请求')
        return
      }

      // 确保有选中的用户
      if (!this.selectedUser || !this.selectedUser.id) {
        console.error('没有选中用户，无法获取列表数据')
        this.$message.error('请先选择一个用户')
        return
      }

      this.loading = true
      console.log('开始加载数据，用户ID:', this.selectedUser.id)

      let params = {
        idTrustedUser: this.selectedUser.id,
        idLast: isLoadMore ? this.lastId : 0,
        limit: 15,
        statuses: [],
        types: []
      }

      // 状态
      if (status === 'moderation') {
        params.statuses = ['on_moderation']
      } else if (status === 'suggested') {
        params.statuses = ['suggested']
      } else if (status === 'declined') {
        params.statuses = ['declined']
      }

      console.log('请求参数 - 状态:', status, '状态数组:', params.statuses)

      // 类型
      if (this.optionsValue === 'all') {
        params.types = ['mail', 'message']
      } else if (this.optionsValue === 'mail') {
        params.types = ['mail']
      } else if (this.optionsValue === 'chat') {
        params.types = ['message']
      }

      getIcebreakersList(params).then(res => {
        console.log(res, '列表')

        if (res.data && res.data.items) {
          const items = res.data.items

          // 判断是否有下一页
          this.hasMore = items.length === 15

          // 更新lastId为最后一条数据的id
          if (items.length > 0) {
            this.lastId = items[items.length - 1].id
          }

          // 处理数据格式转换
          const formattedItems = items.map(item => ({
            id: item.id,
            created: new Date(item.dateCreated),
            type: item.type,
            gender: item.gender,
            message: item.content,
            status: item.status,
            moods: this.formatMoodsForDisplay(item.moods || []),
            declineReasons: item.declineReasons || [],
            attachments: item.attachments || []
          }))

          // 根据状态更新对应的列表
          if (isLoadMore) {
            // 加载更多时追加数据
            if (status === 'moderation') {
              this.moderationList.push(...formattedItems)
            } else if (status === 'suggested') {
              this.suggestedList.push(...formattedItems)
            } else if (status === 'declined') {
              this.declinedList.push(...formattedItems)
            }
          } else {
            // 首次加载或切换时替换数据
            if (status === 'moderation') {
              this.moderationList = formattedItems
            } else if (status === 'suggested') {
              this.suggestedList = formattedItems
            } else if (status === 'declined') {
              this.declinedList = formattedItems
            }
          }
        }

        this.loading = false
      }).catch(err => {
        console.error('获取列表失败:', err)
        this.loading = false
      })
    },
    // 滚动加载更多
    loadMore() {
      if (!this.hasMore || this.loading) return

      this.getIcebreakersListFun(this.currentSubTab, true)
    },
    // 处理滚动事件
    handleScroll(event) {
      const target = event.target
      const scrollTop = target.scrollTop
      const scrollHeight = target.scrollHeight
      const clientHeight = target.clientHeight

      // 当滚动到底部时触发加载更多
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        this.loadMore()
      }
    },
    // 格式化心情数据用于显示
    formatMoodsForDisplay(moods) {
      const moodMap = {
        'real_love': { type: 'realLove', label: 'Real love' },
        'romance': { type: 'romance', label: 'Romance' },
        'flirt': { type: 'flirt', label: 'Flirt' },
        'friendship': { type: 'friendship', label: 'Friendship' },
        'hot_talks': { type: 'hotTalks', label: 'Hot talks' },
        'not_sure': { type: 'notSure', label: 'Not sure' }
      }

      return moods.map(mood => {
        const moodInfo = moodMap[mood.code]
        return {
          type: moodInfo ? moodInfo.type : mood.code,
          label: moodInfo ? moodInfo.label : mood.code,
          status: mood.status,
          isStarred: mood.isStarred
        }
      })
    },
    submitForModeration() {
      if (!this.isFormValid) return

      // 转换心情数据格式为后端需要的格式
      const selectedMoods = Object.keys(this.formData.moods)
        .filter(key => this.formData.moods[key])
        .map(key => {
          const moodMap = {
            'realLove': 'real_love',
            'romance': 'romance',
            'flirt': 'flirt',
            'friendship': 'friendship',
            'hotTalks': 'hot_talks',
            'notSure': 'not_sure'
          }
          return moodMap[key] || key
        })

      // 确保有选中的用户
      if (!this.selectedUser || !this.selectedUser.id) {
        console.error('没有选中用户，无法创建icebreaker')
        this.$message.error('请先选择一个用户')
        return
      }

      const submitData = {
        idTrustedUser: this.selectedUser.id,
        content: this.formData.message,
        type: this.formData.type === 'chat' ? 'message' : this.formData.type
      }

      // 只有聊天类型才需要 gender 和 moods 参数
      if (this.formData.type === 'chat') {
        submitData.gender = this.formData.gender === 'everyone' ? 'all' : this.formData.gender
        submitData.moods = selectedMoods
      }

      // 聊天类型且有图片时，添加图片数据
      if (this.formData.type === 'chat' && this.formData.images && this.formData.images.length > 0) {
        const image = this.formData.images[0];
        submitData.idPhoto = image.idPhoto || image.id || '';
      }
      console.log(submitData, 'submitData')
      // return
      createIcebreakers(submitData).then(res => {
        console.log('创建icebreakers:', res)
        if (res.error) {
          this.$message.error(res.error)
          return
        }
        this.$message.success('创建成功')
        console.log('Submit data:', submitData)
        this.resetForm()
      })
    },
    resetForm() {
      this.formData = {
        type: 'chat',
        message: '',
        moods: {
          realLove: false,
          romance: false,
          flirt: false,
          friendship: false,
          hotTalks: false,
          notSure: false
        },
        gender: 'everyone',
        images: []
      }
    },
    insertEmoji(emoji) {
      // 将表情插入到消息内容中
      this.formData.message += emoji;
    },
    checkDailyLimit() {
      // 调用真实的API获取每日限制
      this.getIcebreakersLimitsFun()
    },
    getAvailableMoods() {
      // 只返回当前显示的心情选项
      if (this.formData.gender === 'everyone') {
        return ['realLove', 'friendship', 'hotTalks'];
      } else {
        return ['realLove', 'hotTalks'];
      }
    },
    toggleMood(moodType) {
      // 如果尝试选择友谊但性别不是"所有人"，则不允许选择
      if (moodType === 'friendship' && this.formData.gender !== 'everyone') {
        this.$message.warning('友谊选项仅在接收者为"所有人"时可用');
        return;
      }
      this.formData.moods[moodType] = !this.formData.moods[moodType]
    },
    toggleSelectAll() {
      const shouldSelectAll = !this.isAllSelected
      const availableMoods = this.getAvailableMoods();
      availableMoods.forEach(mood => {
        this.formData.moods[mood] = shouldSelectAll;
      });
    },
    formatDate(date) {
      const options = {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return new Intl.DateTimeFormat('en-US', options).format(date);
    },
    // 打开附件选择模态框
    openAttachmentModal() {
      if (this.formData.type === 'mail') {
        return;
      }
      if (!this.canAddImage) {
        this.$message.warning('最多只能上传1张图片');
        return;
      }

      // 打开附件选择器
      this.$refs.attachmentDom.init();
    },
    // 处理附件选择
    handleAttachmentSelection(data) {
      if (this.formData.type === 'mail') {
        return;
      }
      console.log('handleAttachmentSelection called with:', data);
      const selectedItems = data.dataMsgList || [];
      const mediaType = data.bTypeVal; // 'image' 或 'video'
      console.log('selectedItems:', selectedItems);
      console.log('mediaType:', mediaType);

      if (selectedItems && selectedItems.length > 0) {
        // 只取第一张图片
        const firstImage = selectedItems[0];
        this.formData.images = [firstImage];
        console.log('添加图片:', firstImage);
      }
    },
    // 处理附件模态框可见性变化
    handleAttachmentVisibleChange(visible) {
      // 可以在这里处理模态框关闭时的逻辑
      if (!visible) {
        console.log('附件选择器已关闭');
      }
    },
    // 获取媒体URL
    getMediaUrl(file) {
      return file.url || file.originalUrl || file;
    },
    // 删除图片
    removeImage(index) {
      this.formData.images.splice(index, 1);
    },
    getGenderIconClass(gender) {
      switch (gender) {
        case 'female':
          return 'el-icon-female';
        case 'male':
          return 'el-icon-male';
        case 'all':
          return 'el-icon-user-solid';
        default:
          return 'el-icon-user-solid';
      }
    },
    getGenderTooltip(gender) {
      const genderTooltips = {
        'female': '女性',
        'male': '男性',
        'all': '所有人'
      }
      return genderTooltips[gender] || '所有人'
    },
    getMoodIconClass(moodType) {
      switch (moodType) {
        case 'realLove':
        case 'real_love':
          return 'el-icon-heart';
        case 'romance':
          return 'el-icon-star-on';
        case 'flirt':
          return 'el-icon-view';
        case 'friendship':
          return 'el-icon-handshake';
        case 'hotTalks':
        case 'hot_talks':
          return 'el-icon-star-on';
        case 'notSure':
        case 'not_sure':
          return 'el-icon-question';
        default:
          return '';
      }
    },
    getImageUrls(attachments) {
      if (!attachments || !Array.isArray(attachments)) {
        return []
      }
      return attachments
        .filter(attachment => attachment.type === 'photo' && attachment.url)
        .map(attachment => attachment.url)
    }
  }
}
</script>

<style scoped lang="scss">
.create-section {
  background: white;
  border-radius: 0 0 8px 8px;
  overflow: visible;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.sub-nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.sub-nav-launch {
  display: flex;
  gap: 32px;
  padding: 20px 24px;
}

.sub-nav-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #64748b;
  padding: 8px 0;
  position: relative;
}

.sub-nav-btn.active {
  color: #3730a3;
  font-weight: 600;
}

.sub-nav-btn.active::after {
  content: '';
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #3730a3 0%, #581c87 100%);
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(55, 48, 163, 0.4);
}

.form-container {
  padding: 32px;
  flex: 1;
}

.warning-banner {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  color: #78350f;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
}

.form-section {
  margin-bottom: 32px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
}

.radio-item.active {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;
}

.radio-item input[type="radio"] {
  display: none;
}

.radio-custom {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
}

.radio-item.active .radio-custom {
  border-color: #0ea5e9;
  background: #0ea5e9;
}

.radio-item.active .radio-custom::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  margin-top: -3px;
  margin-left: -3px;
}

.radio-text {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

/* 标签行样式 */
.labels-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .form-label {
    margin-bottom: 0;
  }
}

/* 消息和图片容器 */
.message-and-image-container {
  display: flex;
  gap: 20px;
  align-items: stretch;
  /* 改为stretch，让两个区域高度一致 */

  &.with-image {
    .message-input-section {
      flex: 1;
    }

    .image-upload-section {
      flex: 0 0 200px;
    }
  }
}

.message-input-section {
  &.full-width {
    width: 100%;
  }
}

.message-input-container {
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  box-sizing: border-box;
  padding: 12px;

  &:focus-within {
    border: 2px solid #1f2937;
  }
}

.message-input {
  width: 100%;
  height: 100px;
  border: transparent;
  font-size: 14px;
  line-height: 1.6;
  outline: none;
  resize: none;

  &:focus {
    border: transparent;
    outline: none;
  }
}

.message-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding: 0 4px;
}

.emoji-picker {
  cursor: pointer;
  font-size: 16px;
}

.char-count {
  font-size: 12px;
  color: #6b7280;
}

.mood-selectors {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.mood-selector-item {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.mood-selector-item .icebreaker-select {
  width: 100%;
  flex: 1;
}

.select-all {
  color: #3730a3;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.select-all:hover {
  text-decoration: underline;
}

.mood-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 25px;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  min-width: 140px;
  flex-shrink: 0;
  border: 2px solid transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mood-label.real-love {
  background: #dbeafe;
  border-color: #93c5fd;
}

.mood-label.romance {
  background: #fce7f3;
  border-color: #f9a8d4;
}

.mood-label.flirt {
  background: #fef3c7;
  border-color: #fbbf24;
}

.mood-label.friendship {
  background: #dcfce7;
  border-color: #86efac;
}

.mood-label.hot-talks {
  background: #f3e8ff;
  border-color: #c084fc;
}

.mood-label.not-sure {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.mood-label.selected {
  border: 3px solid #1f2937;
  box-shadow: 0 4px 12px rgba(31, 41, 55, 0.3);
}

.mood-label.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.disabled-hint {
  font-size: 10px;
  color: #6b7280;
  margin-left: 4px;
}

.mood-selector-item.disabled {
  cursor: not-allowed;
}

.mood-icon {
  font-size: 18px;
  line-height: 1;
}

// 卡片式布局样式
.list-container {
  padding: 20px;
}

.icebreaker-card {
  display: flex;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-left {
  flex: 1;
  padding: 20px;
  border-right: 1px solid #e5e7eb;
}

.card-right {
  width: 150px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.card-header {
  display: grid;
  grid-template-columns: 80px 60px 80px 1fr;
  gap: 16px;
  align-items: start;
}

.card-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.col-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
}

.col-value {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.message-col {
  grid-column: 4;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 图片附件样式 */
.attachment-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
}

.attachment-item {
  display: flex;
  align-items: center;
}

.image-attachment {
  position: relative;
}

.attachment-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.message-text {
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
  margin: 0;
}

.message-emoji {
  font-size: 16px;
}

.mood-tags {
  display: flex;
  // flex-direction: column;
  gap: 4px;
}

.mood-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  border: 1px solid;

  &.realLove,
  &.real_love {
    background-color: #dbeafe;
    border-color: #93c5fd;
  }

  &.romance {
    background-color: #fce7f3;
    border-color: #f9a8d4;
  }

  &.flirt {
    background-color: #fef3c7;
    border-color: #fbbf24;
  }

  &.friendship {
    background-color: #dcfce7;
    border-color: #86efac;
  }

  &.hotTalks,
  &.hot_talks {
    background-color: #f3e8ff;
    border-color: #c084fc;
  }

  &.notSure,
  &.not_sure {
    background-color: #f3f4f6;
    border-color: #d1d5db;
  }
}

.status-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  text-align: center;

  &.pending {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
  }

  &.approved {
    background-color: #d1fae5;
    border: 1px solid #10b981;
  }

  &.declined {
    background-color: #fee2e2;
    border: 1px solid #ef4444;
  }
}

.status-text {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.decline-reason {
  font-size: 12px;
  color: #6b7280;
}

.disagree-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }
}

.refresh-icon {
  width: 12px;
  height: 12px;
  background: #6b7280;
  border-radius: 50%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 8px;
    height: 8px;
    border: 1px solid white;
    border-radius: 50%;
  }
}

.type-icon {
  width: 16px;
  height: 16px;
  background: #6b7280;
  border-radius: 50%;

  &.chat-icon {
    background: #3b82f6;
  }

  &.mail-icon {
    background: #10b981;
  }
}

.gender-icon {
  width: 16px;
  height: 16px;
  background: #6b7280;
  border-radius: 50%;

  &.female-icon {
    background: #ec4899;
  }

  &.male-icon {
    background: #3b82f6;
  }

  &.everyone-icon {
    background: #8b5cf6;
  }
}

.mood-selector-item.select-all {
  background: #10b981;
}

.bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
  margin-top: 32px;
}

.limit-info {
  font-size: 14px;
  color: #6b7280;
}

.submit-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3730a3 0%, #581c87 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(55, 48, 163, 0.5);
}

.submit-btn:hover:not(.disabled) {
  box-shadow: 0 6px 20px rgba(55, 48, 163, 0.6);
}

.submit-btn.disabled {
  background: #d1d5db;
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn.disabled:hover {
  box-shadow: none;
}

.icon-class {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #6b7280;
  font-size: 14px;
  gap: 8px;
}

.no-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #9ca3af;
  font-size: 14px;
}

.list-container {
  max-height: 600px;
  overflow-y: auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #9ca3af;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #d1d5db;
}

.empty-state p {
  font-size: 18px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 8px 0;
}

.empty-state span {
  font-size: 14px;
  color: #9ca3af;
}

/* 用户信息横幅样式 */
.user-info-banner {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid rgba(55, 48, 163, 0.2);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(55, 48, 163, 0.1);
}

.user-info-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-info-content .user-avatar {
  position: relative;
  flex-shrink: 0;
}

.user-info-content .avatar-img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-info-content .online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #d1d5db;
  border: 2px solid white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.user-info-content .online-indicator.online {
  background: #10b981;
  box-shadow: 0 1px 4px rgba(16, 185, 129, 0.4);
}

.user-info-content .user-details {
  flex: 1;
  min-width: 0;
}

.user-info-content .user-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-info-content .user-id {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.user-info-content .user-status {
  flex-shrink: 0;
}

.user-info-content .vip-icon {
  color: #fbbf24;
  font-size: 18px;
  text-shadow: 0 1px 2px rgba(251, 191, 36, 0.3);
}

/* 图片上传相关样式 */
.image-upload-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
}

/* 侧边栏图片上传区域样式 */
.image-upload-section {
  display: flex;
  align-items: stretch;

  .image-upload-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 100px;
    /* 与文本输入区域的最小高度匹配 */
  }

  .upload-area {
    width: 169px;
    height: 169px;
  }

  .image-preview {
    width: 169px;
    height: 169px;
  }
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 169px;
  height: 169px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: #f9fafb;
  cursor: pointer;
  position: relative;
}

.upload-area:hover:not(.disabled) {
  border-color: #3730a3;
  background: #f0f9ff;
  box-shadow: 0 4px 12px rgba(55, 48, 163, 0.15);
}

.upload-area.disabled {
  border-color: #e5e7eb;
  background: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.6;
}

.upload-icon {
  color: #6b7280;
  margin-bottom: 8px;
}

.upload-area:hover:not(.disabled) .upload-icon {
  color: #3730a3;
}

.upload-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
  text-align: center;
}

.upload-hint {
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  line-height: 1.2;
}

.image-preview {
  position: relative;
  width: 169px;
  height: 169px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-image {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
}

.image-preview:hover .remove-image {
  opacity: 1;
}

.remove-image:hover {
  background: rgba(220, 38, 38, 1);
}

/* 确保attachment组件的dialog在最顶层 */
::v-deep .attachment .el-dialog {
  z-index: 10000 !important;
}

::v-deep .attachment .el-dialog__wrapper {
  z-index: 10000 !important;
}

/* 设置dialog的位置和高度 */
::v-deep .el-dialog {
  margin-top: 5vh !important;

  .el-dialog__body {
    height: calc(90vh - 10px);
    box-sizing: border-box;
  }
}
</style>